import { Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { frameworks } from '@/content/compliance';
import { useSeo } from '@/seo/useSeo';

export function Compliance(): JSX.Element {
  useSeo({
    title: 'Compliance frameworks for MSPs',
    description:
      'How Nexus tracks posture against SOC 2, NIST CSF, HIPAA, PCI-DSS, ISO 27001, and FERPA/COPPA — three-state control tracking, evidence tied to each control, and an automated QBR rollup.',
    path: '/compliance',
    schema: {
      '@type': 'ItemList',
      itemListElement: frameworks.map((f, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://nexusrmm.ai/compliance/${f.slug}`,
        name: f.name,
      })),
    },
  });
  return (
    <div>
      <PageHero
        eyebrow="Compliance, by framework"
        title="Whatever framework your clients answer to, it reaches you too."
        subtitle="Compliance posture tracking isn't one generic dashboard — it's control-level tracking mapped to the specific frameworks your clients actually answer to. Pick one below."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {frameworks.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.slug} delay={(i % 3) * 80}>
                  <Link
                    to={`/compliance/${f.slug}`}
                    className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                  >
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                      <Icon fontSize="small" />
                    </div>
                    <h2 className="font-display text-[17px] font-semibold text-nexus-ink">{f.name}</h2>
                    <p className="mt-1.5 flex-1 text-sm text-white/65">{f.summary}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={200}>
            <p className="mx-auto mt-10 max-w-[640px] text-center text-xs text-white/40">
              These pages describe what the Nexus compliance module tracks against each framework —
              not a claim that Nexus, or any client running on it, is certified or audited. See the{' '}
              <Link to="/platform/compliance-qbr" className="text-white/50 underline hover:text-white/70">
                compliance &amp; QBR module
              </Link>{' '}
              for how the tracking actually works.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                See your framework&apos;s posture built for real.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Join the design-partner cohort and we&apos;ll walk through what tracking looks like
                for the specific frameworks your clients answer to.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
